import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/shared/service/auth.service';
import { EcklerRouteConfig } from 'src/app/shared/eckler-route-config';
import { UserTypeEnum } from 'src/app/shared/enum/user-type-enum';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { switchMap, tap, catchError, first } from 'rxjs/operators';
import { of } from 'rxjs';
import { LanguageService, LOGIN_LANGUAGE_KEY } from 'src/app/shared/service/language.service';

@Component({
  selector: 'app-auth-callback',
  template: `
    <div class="loading-container">
      <mat-spinner></mat-spinner>
      <p>{{ 'PROCESSING_AUTHENTICATION' | translate }}</p>
    </div>
  `,
  styles: [`
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      gap: 20px;
    }
    
    p {
      font-size: 16px;
      color: #666;
    }
  `]
})
export class AuthCallbackComponent implements OnInit {

  constructor(
    private authService: AuthService,
    private auth0: Auth0Service,
    private router: Router,
    private route: ActivatedRoute,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    // Get the authorization code from the URL parameters
    const code = this.route.snapshot.queryParamMap.get('code');
    const state = this.route.snapshot.queryParamMap.get('state');
    const error = this.route.snapshot.queryParamMap.get('error');

    console.log('Auth0 callback parameters:', { code, state, error });

    if (error) {
      console.error('Auth0 callback error:', error);
      this.redirectToLogin();
      return;
    }

    if (code) {
      // Handle the Auth0 callback - let Auth0 SDK handle the token exchange
      this.handleAuth0Callback();
    } else {
      // No code parameter, redirect to login
      this.redirectToLogin();
    }
  }

  private handleAuth0Callback(): void {
    // Set authentication in progress flag
    this.authService.setAuthenticationInProgress(true);

    // Use RxJS operators to flatten the authentication flow
    // This prevents race conditions with route guards
    this.auth0.isAuthenticated$.pipe(
      first(),
      switchMap(isAuthenticated => {
        console.log('Auth0 isAuthenticated:', isAuthenticated);
        if (!isAuthenticated) {
          throw new Error('Not authenticated');
        }
        return this.auth0.user$;
      }),
      first(),
      switchMap(user => {
        if (!user) {
          throw new Error('No user found');
        }
        console.log('Auth0 user:', user);
        return this.auth0.getAccessTokenSilently().pipe(
          switchMap(token => {
            // Exchange Auth0 token with backend
            return this.authService.exchangeAuth0Token(token, user).pipe(
              tap(authResult => {
                // Store the authentication result
                localStorage.setItem('ecklerUser', JSON.stringify(authResult));

                // Resolve language: member-level → plan-level fallback → English default
                // member.Language is 0 when never explicitly set (C# enum uninitialized)
                const memberLangSet = authResult.language === 1 || authResult.language === 2;
                const resolvedLanguage = memberLangSet
                  ? authResult.language
                  : (authResult.planLanguage ?? 1); // fall back to plan, then English

                const source = memberLangSet ? 'member-preference' : (authResult.planLanguage != null ? 'plan-fallback' : 'default-english');

                if (!memberLangSet) {
                  this.languageService.log('language-fallback-triggered', {
                    reason: 'member-language-not-set',
                    planLanguage: authResult.planLanguage,
                    resolvedLanguage,
                    source
                  });
                }

                this.languageService.applyLanguage(resolvedLanguage);
                this.languageService.persistPreference(resolvedLanguage);
                this.languageService.log('language-applied-post-auth', {
                  source,
                  language: resolvedLanguage
                });

                // Detect mismatches between the login-page language and the
                // member's stored preference (e.g. preference changed externally).
                const loginLangRaw = sessionStorage.getItem(LOGIN_LANGUAGE_KEY);
                if (loginLangRaw !== null) {
                  const loginLang = parseInt(loginLangRaw, 10);
                  if (loginLang !== authResult.language) {
                    this.languageService.log('language-mismatch', {
                      loginPageLanguage: loginLang,
                      memberLanguage: authResult.language,
                      info: 'Auth0 login page may have shown a different language than the member preference'
                    });
                  }
                  sessionStorage.removeItem(LOGIN_LANGUAGE_KEY);
                }

                // CRITICAL: Set current user in auth service BEFORE navigation
                // This ensures auth guards can evaluate correctly
                this.authService.updateCurrentUser(authResult);
              })
            );
          })
        );
      }),
      catchError(error => {
        console.error('Authentication flow error:', error);
        this.authService.setAuthenticationInProgress(false);
        
        // Check if the error requires Auth0 logout
        if (error.error && error.error.requiresAuth0Logout) {
          console.log('User not found in system, performing Auth0 logout...');
          this.authService.signOut();
        } else {
          this.redirectToLogin();
        }
        return of(null);
      })
    ).subscribe(authResult => {
      if (authResult) {
        // Clear authentication in progress flag
        console.log('Authentication successful, redirecting user...');
        this.authService.setAuthenticationInProgress(false);
        console.log('Auth result from backend:', authResult);
        // Backend authentication successful, redirect to appropriate dashboard
        this.redirectUser(authResult);
      }
    });
  }

  private redirectUser(authResult: any): void {
    // Create user from auth result and set in auth service
    const user = this.authService.createUserFromAuthenticateResult(authResult);
    
    let targetRoute: string[];

    let userType: UserTypeEnum = UserTypeEnum[authResult.userType as keyof typeof UserTypeEnum];
    
    if (userType === UserTypeEnum.SuperUser) {
      targetRoute = [EcklerRouteConfig.ecklerPlanListRoute()];
    } else if (userType === UserTypeEnum.Administrator) {
      targetRoute = [EcklerRouteConfig.planPlanMembersListRoute()];
    } else if (userType === UserTypeEnum.Member) {
      targetRoute = [EcklerRouteConfig.planMemberPortalRoute()];
    } else {
      // Fallback to login if user type is not recognized
      this.redirectToLogin();
      return;
    }
    
    // Use replaceUrl to avoid polluting browser history with callback URL
    // This provides a seamless transition without showing intermediate states
    this.router.navigate(targetRoute, { replaceUrl: true });
  }

  private redirectToLogin(): void {
    this.router.navigate([EcklerRouteConfig.loginRoute('')]);
  }
}